import {Editor, useEditorState} from '@tiptap/react';
import {Tooltip, TooltipRefProps} from 'react-tooltip';
import {Button, Label, TextInput} from '@trussworks/react-uswds';
import {useEffect, useId, useRef, useState} from 'react';
import ButtonWithTooltip from '../../FormComponents/ButtonWithTooltip/ButtonWithTooltip';

const allowed_protocols = [
  'http',
  'https',
  'ftp',
  'ftps',
  'mailto',
  'tel',
  'callto',
  'sms',
  'cid',
  'xmpp',
];

function sanitizeUri(uri: string) {
  try {
    const parsed_uri = new URL(uri);
    if (allowed_protocols.includes(parsed_uri.protocol.slice(0, -1))) {
      return parsed_uri.href;
    }
  } catch {
    // Invalid URI
  }
  return '';
}

interface Props {
  editor: Editor;
  anchor_select: string;
  setIsOpen?: (is_open: boolean) => void;
}

export default function TiptapLinkPopover({editor, anchor_select, setIsOpen}: Props) {
  const tooltip_ref = useRef<TooltipRefProps>(null);
  const input_id = useId();
  const input_ref = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  const [is_url_error, setIsUrlError] = useState(false);

  const close = () => tooltip_ref.current?.close();

  const editor_state = useEditorState({
    editor,
    selector: ({editor}) => {
      const state = {is_link: false};

      if (!editor.isInitialized || !editor.isEditable) {
        return state;
      }

      state.is_link = editor.isActive('link');
      return state;
    },
  });

  useEffect(() => {
    const updateLinkState = () => {
      setIsUrlError(false);
      const href = editor.getAttributes('link').href as unknown;
      setUrl(typeof href == 'string' ? href : '');
    };

    editor.on('selectionUpdate', updateLinkState);
    return () => {
      editor.off('selectionUpdate', updateLinkState);
    };
  }, [editor]);

  const setLink = () => {
    if (!url || !editor.isEditable) return;

    const href = sanitizeUri(url);
    if (!href) {
      setIsUrlError(true);
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({href}).run();
    if (editor.state.selection.empty) {
      editor.chain().focus().insertContent({type: 'text', text: url}).run();
    }

    close();
  };

  const removeLink = () => {
    if (!editor.isEditable) return;

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .unsetLink()
      .setMeta('preventAutolink', true)
      .run();

    setUrl('');
    close();
  };

  return (
    <Tooltip
      ref={tooltip_ref}
      anchorSelect={anchor_select}
      place="bottom-start"
      clickable
      variant="light"
      style={{padding: '0.5rem 0 0.5rem 0.5rem'}}
      openOnClick
      globalCloseEvents={{escape: true}}
      closeEvents={{click: true}}
      setIsOpen={setIsOpen}
      border="1px solid #1c6da6"
      opacity={1}
      className="display-flex"
      afterShow={() => input_ref.current?.focus({preventScroll: true})}
    >
      <Label srOnly htmlFor={input_id}>
        URL
      </Label>
      <TextInput
        id={input_id}
        name={input_id}
        ref={input_ref}
        placeholder="URL"
        type="url"
        className="margin-top-0-important height-4 border-right-0 flex-fill"
        value={url}
        validationStatus={is_url_error ? 'error' : undefined}
        onChange={(event) => {
          setIsUrlError(false);
          setUrl(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key == 'Enter') {
            event.preventDefault();
            setLink();
          }
        }}
      />
      <Tooltip anchorSelect={`#${CSS.escape(input_id)}`} place="bottom" isOpen={is_url_error}>
        Please enter a valid URL
      </Tooltip>
      <Button
        type="button"
        className="margin-top-0-important margin-0 padding-x-105 padding-y-1 height-4 radius-left-0 radius-right-md width-auto"
        onClick={setLink}
      >
        Set
      </Button>
      <ButtonWithTooltip
        onClick={removeLink}
        is_disabled={!editor_state.is_link}
        className="margin-top-0-important margin-left-105 margin-right-0 height-4 padding-y-1 padding-x-105 usa-button--secondary width-auto"
        tooltip_text="Remove link"
        tooltip_place="top"
        is_small_tooltip
      >
        {
          // Google Material Design Icon: Link Off (Apache License Version 2.0)
          // https://fonts.google.com/icons?icon.query=link&icon.size=24&icon.color=%23000000&selected=Material+Symbols+Outlined:link_off:FILL@0;wght@400;GRAD@0;opsz@24
        }
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="1rem"
          width="1rem"
          viewBox="0 -960 960 960"
          fill="currentColor"
        >
          <path d="m770-302-60-62q40-11 65-42.5t25-73.5q0-50-35-85t-85-35H520v-80h160q83 0 141.5 58.5T880-480q0 57-29.5 105T770-302ZM634-440l-80-80h86v80h-6ZM792-56 56-792l56-56 736 736-56 56ZM440-280H280q-83 0-141.5-58.5T80-480q0-69 42-123t108-71l74 74h-24q-50 0-85 35t-35 85q0 50 35 85t85 35h160v80ZM320-440v-80h65l79 80H320Z" />
        </svg>
      </ButtonWithTooltip>
      <Button
        type="button"
        aria-label="Close"
        unstyled
        className="margin-top-0-important margin-x-05 height-4 padding-y-1 padding-x-1 width-auto"
        onClick={close}
      >
        {
          // Google Material Design Icon: Close (Apache License Version 2.0)
          // https://fonts.google.com/icons?icon.query=close&icon.size=24&icon.color=%23000000&selected=Material+Symbols+Outlined:close:FILL@0;wght@400;GRAD@0;opsz@24
        }
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="1rem"
          width="1rem"
          viewBox="0 -960 960 960"
          fill="currentColor"
        >
          <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
        </svg>
      </Button>
    </Tooltip>
  );
}
